/**
 * @CODE:ACC-003-HOOKS-001
 * useAccommodationDetail Hook
 *
 * Custom hook for fetching single accommodation details
 * Features:
 * - Fetches accommodation by ID using TanStack Query
 * - 5 minute staleTime for caching
 * - Automatic refetch on ID change
 * - Loading and error state management
 */

import { useQuery } from '@tanstack/react-query';
import { getAccommodationById } from '../api/accommodation.api';
import type { AccommodationWithDetails } from '../types';

interface UseAccommodationDetailReturn {
  data: AccommodationWithDetails | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Hook to fetch accommodation details by ID
 * @param accommodationId - The accommodation ID (UUID) to fetch
 * @returns Object with accommodation data, loading state, and error state
 */
export function useAccommodationDetail(
  accommodationId: string
): UseAccommodationDetailReturn {
  const query = useQuery<AccommodationWithDetails>({
    queryKey: ['accommodation', accommodationId],
    queryFn: () => getAccommodationById(accommodationId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
  };
}
